import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { GoogleLogo, EnvelopeSimpleOpen } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("email@example.com");
  const [password, setPassword] = useState("password");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    if (!accepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy first.");
      return;
    }
    // Mark acceptance so the post-redirect /auth/google/session call forwards it.
    localStorage.setItem("pending_tos_accept", "1");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-foreground text-background p-12">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <img src="/easy-mail-logo.png" alt="Easy Mail" className="h-7 w-auto" />
          <span className="font-display text-xl font-bold">Easy Mail</span>
        </Link>
        <div>
          <p className="font-display text-5xl leading-none mb-6">Bring order to the chaos.</p>
          <p className="text-base text-background/70 max-w-md">Bulk delete. Manage subscriptions. Set Auto-Rules. Reclaim hours. Privacy by design.</p>
        </div>
        <div className="font-mono text-xs uppercase tracking-widest text-background/50">v0.1 — Public Beta</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="font-display text-4xl mb-2">Sign in</h1>
          <p className="text-muted-foreground mb-8">Welcome back. Let's clean your inbox.</p>

          <label className="flex items-start gap-3 mb-5 text-sm cursor-pointer" data-testid="tos-row">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="rounded-none mt-0.5" data-testid="tos-checkbox" />
            <span>
              I agree to the <Link to="/legal/terms_and_conditions" className="underline">Terms &amp; Conditions</Link> and <Link to="/legal/privacy_policy" className="underline">Privacy Policy</Link>.
            </span>
          </label>

          <Button
            type="button"
            onClick={googleSignIn}
            variant="outline"
            disabled={!accepted}
            className="w-full rounded-none gap-2 mb-4 h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="google-signin-btn"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </Button>
          <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-muted-foreground my-4">
            <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-none" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="pw">Password</Label>
              <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-none" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-none h-11 btn-brutalist" data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6">
            No account? <Link to="/register" className="underline" data-testid="link-register">Create one</Link>
          </p>
        </div>
      </div>
    <div data-testid="footer-legal" className="border-t border-border py-6 text-center text-xs text-muted-foreground"><a href="/legal" className="underline mx-2">Legal Documents and Policies</a></div></div>
  );
}
